import { Ionicons } from '@expo/vector-icons';
import { router, type Href } from 'expo-router';
import React, { useState } from 'react';
import { Pressable, View } from 'react-native';

import { useNotification } from '@/components/notification-system';
import { AccountTypeahead } from '@/components/search/account-typeahead';
import { ActivityIndicator, Button, Modal, Text, useTheme } from '@/components/ui';
import { useComplaintTargets, useSetComplaintTarget, useSetReportedToBoard } from '@/hooks/use-complaints';
import type { Account } from '@/types/models';
import { friendlyApiMessage } from '@/utils/api-error';
import { etDayMs, formatBusinessDate } from '@/utils/date-utils';

export interface ComplaintPanelProps {
	messageId: string;
	/** The complaint's association — the only one its target units may come from. */
	associationId: string | null;
	reportedToBoardAt: string | null | undefined;
	canUpdate: boolean;
}

/**
 * What a complaint is AGAINST, and whether the board has seen it (#566).
 *
 * The complaint stays filed under whoever sent it — that is the Homeowner / Lot row above.
 * This panel adds the OTHER side: the units the complaint is about, picked through the same
 * account typeahead the accounting forms use, in a modal rather than an inline form.
 */
export const ComplaintPanel: React.FC<ComplaintPanelProps> = ({
	messageId,
	associationId,
	reportedToBoardAt,
	canUpdate,
}) => {
	const theme = useTheme();
	const { showModal, showToast } = useNotification();
	const { data: targets, isLoading } = useComplaintTargets(messageId);
	const setTarget = useSetComplaintTarget();
	const setReported = useSetReportedToBoard();
	const [pickerOpen, setPickerOpen] = useState(false);
	const [picked, setPicked] = useState<Account | null>(null);

	const fail = (error: unknown, fallback: string) =>
		showModal({ title: 'Error', message: friendlyApiMessage(error, fallback), variant: 'error' });

	const addTarget = async () => {
		if (!picked) return;
		try {
			await setTarget.mutateAsync({ messageId, accountId: picked.id, on: true });
			setPickerOpen(false);
			setPicked(null);
			showToast('Unit added to the complaint', { variant: 'success' });
		} catch (error) {
			fail(error, 'Could not add that unit.');
		}
	};

	const removeTarget = async (accountId: string) => {
		try {
			await setTarget.mutateAsync({ messageId, accountId, on: false });
		} catch (error) {
			fail(error, 'Could not remove that unit.');
		}
	};

	const toggleReported = async () => {
		try {
			await setReported.mutateAsync({ messageId, reported: !reportedToBoardAt });
			showToast(reportedToBoardAt ? 'Marked not reported' : 'Marked reported to the board', { variant: 'success' });
		} catch (error) {
			fail(error, 'Could not update the board report.');
		}
	};

	// A real moment; shown as the Eastern calendar day it happened on.
	const reportedOn = reportedToBoardAt ? formatBusinessDate(etDayMs(Date.parse(reportedToBoardAt))) : null;

	return (
		<View
			testID="complaint-panel"
			style={{
				marginTop: 14,
				padding: 12,
				borderRadius: 8,
				borderWidth: 1,
				borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
			}}
		>
			<Text style={{ fontSize: 12, fontWeight: '700', letterSpacing: 0.4, color: theme.colors.onSurfaceVariant, marginBottom: 6 }}>
				COMPLAINT AGAINST
			</Text>
			{isLoading ? <ActivityIndicator size="small" /> : null}
			<View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
				{(targets ?? []).map((target) => (
					<View
						key={target.accountId}
						testID={`complaint-target-${target.accountId}`}
						style={{
							flexDirection: 'row', alignItems: 'center',
							paddingVertical: 4, paddingLeft: 10, paddingRight: canUpdate ? 4 : 10,
							borderRadius: 14, backgroundColor: theme.colors.surfaceVariant,
						}}
					>
						<Pressable
							onPress={() => router.push(`/accounting/account/${target.accountId}` as Href)}
							accessibilityRole="link"
						>
							<Text style={{ fontWeight: '600', color: theme.colors.primary }}>
								{[target.accountNumber, target.ownerName].filter(Boolean).join(' · ') || target.address || 'Unit'}
							</Text>
						</Pressable>
						{canUpdate ? (
							<Pressable
								onPress={() => { void removeTarget(target.accountId); }}
								disabled={setTarget.isPending}
								accessibilityLabel={`Remove ${target.accountNumber ?? 'unit'}`}
								testID={`btn-remove-complaint-target-${target.accountId}`}
								// Real padding, not hitSlop — RN-Web's Pressable drops hitSlop.
								style={{ paddingVertical: 2, paddingHorizontal: 6 }}
							>
								<Ionicons name="close" size={14} color={theme.colors.onSurfaceVariant} />
							</Pressable>
						) : null}
					</View>
				))}
				{!isLoading && (targets ?? []).length === 0 ? (
					<Text style={{ color: theme.colors.onSurfaceVariant }}>No units named yet.</Text>
				) : null}
				{canUpdate ? (
					<Pressable
						onPress={() => setPickerOpen(true)}
						testID="btn-add-complaint-target"
						style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 4, paddingHorizontal: 6 }}
					>
						<Ionicons name="add-circle-outline" size={14} color={theme.colors.primary} style={{ marginRight: 4 }} />
						<Text style={{ fontSize: 12, fontWeight: '600', color: theme.colors.primary }}>Add unit</Text>
					</Pressable>
				) : null}
			</View>

			<View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
				<Ionicons
					name={reportedToBoardAt ? 'checkmark-done-outline' : 'people-outline'}
					size={16}
					color={theme.colors.onSurfaceVariant}
				/>
				<Text testID="complaint-board-status" style={{ color: theme.colors.onSurfaceVariant }}>
					{reportedOn ? `Reported to the board ${reportedOn}` : 'Not yet reported to the board'}
				</Text>
				{canUpdate ? (
					<Pressable
						onPress={() => { void toggleReported(); }}
						disabled={setReported.isPending}
						testID="btn-toggle-reported-to-board"
						style={{ paddingVertical: 4, paddingHorizontal: 6 }}
					>
						<Text style={{ fontSize: 12, fontWeight: '600', color: theme.colors.primary }}>
							{reportedToBoardAt ? 'Undo' : 'Mark reported'}
						</Text>
					</Pressable>
				) : null}
			</View>

			<Modal visible={pickerOpen} onDismiss={() => setPickerOpen(false)}>
				<View style={{ padding: 20, minWidth: 380, maxWidth: 560 }}>
					<Text style={{ fontSize: 18, fontWeight: '700', marginBottom: 4 }}>Add a unit to this complaint</Text>
					<Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 12 }}>
						{associationId
							? 'Search by account number, owner name or address.'
							: 'Set the association on this complaint first — its units come from there.'}
					</Text>
					<AccountTypeahead
						associationId={associationId ?? undefined}
						value={picked}
						onSelect={setPicked}
						showAccountBalance={false}
						// A sold lot's closed account stays live while it carries a balance; the
						// complaint belongs on the buyer's (the server resolves one either way).
						status="active"
						disabled={!associationId}
						autoFocus
						testID="complaint-target-typeahead"
					/>
					<View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
						<Button mode="outlined" onPress={() => { setPickerOpen(false); setPicked(null); }} disabled={setTarget.isPending}>
							Cancel
						</Button>
						<Button
							mode="contained"
							onPress={() => { void addTarget(); }}
							disabled={!picked || setTarget.isPending}
							testID="btn-confirm-complaint-target"
						>
							Add
						</Button>
					</View>
				</View>
			</Modal>
		</View>
	);
};

export default ComplaintPanel;
